import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { authAPI } from '../services/api';
import { buildTenantBaseUrl } from '../utils/supportTenantAccess';

const loginHeroLuxuryFleet = '/login-hero-luxury-fleet.png';

type DevShortcutKey = 'platform' | 'company' | 'support';

type DevShortcutConfig = {
  key: DevShortcutKey;
  label: string;
  description: string;
  email?: string;
  password?: string;
  subdomain: string;
  redirectPath: string;
};

type LoginUser = {
  role: 'user' | 'admin';
  is_support_staff?: boolean;
};

function getShortcutOrigin(subdomain: string): string {
  return buildTenantBaseUrl(subdomain);
}

function getShortcutLoginUrl(shortcut: DevShortcutConfig): string {
  const params = new URLSearchParams({
    devShortcut: shortcut.key,
    redirect: shortcut.redirectPath,
  });

  if (shortcut.email && shortcut.password) {
    params.set('autologin', '1');
  }

  return `${getShortcutOrigin(shortcut.subdomain)}/login?${params.toString()}`;
}

function getPostLoginRedirect(user: LoginUser, requestedRedirect?: string | null): string {
  if (user.is_support_staff) {
    return '/support/tenant-switcher';
  }

  if (requestedRedirect) {
    return requestedRedirect;
  }

  return user.role === 'admin' ? '/admin' : '/';
}

function canDisplayPasswordResetLink(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.trim().toLowerCase();

  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.endsWith('.localhost')
    || hostname.endsWith('.localtest.me')
    || hostname.endsWith('.dev.modalhub.net');
}

function LoginPage() {
  const { login } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const autoLoginAttemptRef = useRef<string | null>(null);

  const devShortcuts = useMemo<Record<DevShortcutKey, DevShortcutConfig>>(
    () => ({
      platform: {
        key: 'platform',
        label: 'Open Global Admin',
        description: 'Jump to the platform admin surface on the globaladmin host.',
        email: process.env.REACT_APP_TEST_PLATFORM_ADMIN_EMAIL,
        password: process.env.REACT_APP_TEST_PLATFORM_ADMIN_PASSWORD,
        subdomain: 'globaladmin',
        redirectPath: '/platform/companies',
      },
      company: {
        key: 'company',
        label: 'Open Tenant Admin',
        description: 'Jump to the default tenant admin dashboard with seeded company credentials.',
        email: process.env.REACT_APP_TEST_COMPANY_ADMIN_EMAIL,
        password: process.env.REACT_APP_TEST_COMPANY_ADMIN_PASSWORD,
        subdomain: 'default',
        redirectPath: '/admin',
      },
      support: {
        key: 'support',
        label: 'Open Support Staff',
        description: 'Jump to the platform host using the seeded support staff account.',
        email: 'operator@modalos.com',
        password: 'Miyazaki21!',
        subdomain: 'globaladmin',
        redirectPath: '/support/tenant-switcher',
      },
    }),
    [],
  );

  const submitLogin = useCallback(async (nextEmail: string, nextPassword: string, redirectPath?: string | null) => {
    setIsLoading(true);

    try {
      const loggedInUser = await login(nextEmail, nextPassword);
      navigate(getPostLoginRedirect(loggedInUser, redirectPath), { replace: true });
    } catch (error) {
      // Error handling is done in the auth context
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(location.search);
    await submitLogin(email, password, params.get('redirect'));
  };

  const handleForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Enter your operator email first');
      return;
    }

    setIsForgotLoading(true);
    setResetLink('');
    try {
      const response = await authAPI.forgotPassword(forgotEmail.trim());
      const nextLink = canDisplayPasswordResetLink() ? response.data?.resetLink || '' : '';
      setResetLink(nextLink);
      if (response.data?.emailDelivery?.success) {
        toast.success('Reset email sent successfully');
      } else {
        toast.success(response.data?.message || 'Reset link prepared');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Unable to start password reset');
    } finally {
      setIsForgotLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shortcutKey = params.get('devShortcut') as DevShortcutKey | null;
    const shouldAutoLogin = params.get('autologin') === '1';
    const redirectPath = params.get('redirect');

    if (!shortcutKey || !shouldAutoLogin) return;
    if (autoLoginAttemptRef.current === location.search) return;

    const shortcut = devShortcuts[shortcutKey];
    if (!shortcut?.email || !shortcut?.password) return;

    const targetOrigin = getShortcutOrigin(shortcut.subdomain);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

    if (currentOrigin !== targetOrigin) {
      window.location.replace(getShortcutLoginUrl(shortcut));
      return;
    }

    autoLoginAttemptRef.current = location.search;
    setEmail(shortcut.email);
    setPassword(shortcut.password);
    void submitLogin(shortcut.email, shortcut.password, redirectPath || shortcut.redirectPath);
  }, [devShortcuts, location.search, submitLogin]);

  return (
    <AuthShell
      appName={branding.app_name}
      logoUrl={branding.logo_url}
      title="Welcome back"
      description="Sign in to access your account"
      heroImageUrl={loginHeroLuxuryFleet}
      heroImageAlt="Luxury vehicles parked outside a modern hotel with a city skyline in the background"
      logoPlacement="inline"
      shellVariant="split"
      accentClassName="bg-[radial-gradient(circle,_rgba(59,130,246,0.2),_transparent_68%)]"
    >
      <form
        className="mt-10 w-full"
        name="operator-login"
        autoComplete="on"
        onSubmit={handleSubmit}
      >
        <div className="space-y-5">
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-on-surface">
              Email address
            </label>
            <div className="relative">
              <EnvelopeIcon className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                className="input-field"
                style={{ paddingLeft: '2.9rem' }}
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-semibold text-on-surface">
              Password
            </label>
            <div className="relative">
              <LockClosedIcon className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="input-field"
                style={{ paddingLeft: '2.9rem', paddingRight: '3rem' }}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => {
                setIsForgotPasswordOpen(true);
                setForgotEmail(email);
                setResetLink('');
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] underline underline-offset-2 hover:opacity-80"
            >
              Forgot password?
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="mt-8">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex w-full items-center justify-center gap-2 rounded-xl py-5 text-lg font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'Sign in'}
          </button>
        </div>
      </form>

      {isForgotPasswordOpen && (
        <div className="auth-subpanel mt-6 w-full space-y-3 p-5 sm:mt-8">
                  <div>
                    <h3 className="text-sm font-semibold text-on-surface">Reset password</h3>
                    <p className="mt-1 text-sm text-on-surface-variant">
                      Request a reset link for this account.
                    </p>
                  </div>
                  <form className="space-y-3" onSubmit={handleForgotPassword}>
                    <div>
                      <label htmlFor="forgot-email" className="mb-1 block text-sm font-medium text-on-surface">
                        Operator email
                      </label>
                      <input
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        required
                        className="input-field"
                        placeholder="Enter the operator email"
                        value={forgotEmail}
                        onChange={(event) => setForgotEmail(event.target.value)}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isForgotLoading}
                      className="btn-secondary flex w-full justify-center disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isForgotLoading ? <LoadingSpinner size="sm" /> : 'Send reset email'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPasswordOpen(false);
                        setResetLink('');
                      }}
                      className="btn-secondary w-full"
                    >
                      Cancel
                    </button>
                  </form>

                  {resetLink && (
                    <div className="auth-subpanel p-4 text-sm text-on-info-container" style={{ background: 'var(--info-container)' }}>
                      <div className="font-semibold">Reset link</div>
                      <a
                        href={resetLink}
                        className="mt-2 block break-all font-medium hover:underline"
                      >
                        {resetLink}
                      </a>
                    </div>
                  )}
        </div>
      )}

    </AuthShell>
  );
}

export default LoginPage;
